import Loading from '@app/components/loading.tsx';
import { CurrencyContext } from '@app/components/price/use-currency.ts';
import { getFragmentData } from '@app/graphql/types';
import { OnlineOrderUserState } from '@app/graphql/types/graphql.ts';
import ApplicationErrorView from '@app/module/error/application-error-view.tsx';
import { useOnlineOrderDetailPageQuery } from '@app/page/online-order/detail/logic/use-online-order-detail-page-query.ts';
import { useOnlineOrderDetailStore } from '@app/page/online-order/detail/logic/use-online-order-detail-store.ts';
import useOnlineOrderUpdateSubscription from '@app/page/online-order/detail/logic/use-online-order-update-subscription.ts';
import FillContactInfoPage from '@app/page/online-order/fill-contact-info/fill-contact-info-page.tsx';
import { BranchFragment } from '@app/page/online-order/model/branch-fragment.ts';
import { OnlineOrderAvailabilityFragment } from '@app/page/online-order/model/online-order-availability-fragment.ts';
import { OnlineOrderBranchConfigurationFragment } from '@app/page/online-order/model/online-order-branch-configuration.ts';
import { OnlineOrderFragment } from '@app/page/online-order/model/online-order-fragment.ts';
import OrderDetailPage from '@app/page/online-order/order-detail-page/order-detail-page.tsx';
import UnavailableOnlineOrder from '@app/page/online-order/product-picker/component/unavailable-online-order.tsx';
import ProductRepickerPage from '@app/page/online-order/product-picker/product-repicker-page.tsx';
import OnlineOrderReviewPage from '@app/page/online-order/review/online-order-review-page.tsx';
import { analytics } from '@app/utils/analytics.ts';
import { loadTemplate, normalizeTemplateEnum } from '@app/utils/template-loader.ts';
import useReloadDetection from '@app/utils/use-reload-detection.ts';
import { captureException } from '@sentry/react';
import { setUserProperties } from 'firebase/analytics';
import { ReactNode, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PageNotFound from '../product-picker/component/page-not-found.tsx';
import WaitingForPaymentPage from '../waiting-for-payment/waiting-for-payment-page.tsx';
import ManualBankTransferPage from '../manual-bank-transfer/manual-bank-transfer-page.tsx';

export const DONE_STAGE_FIRST_ACCESS = 'DONE_STAGE_FIRST_ACCESS';
const OnlineOrderDetailPage = () => {
  const params = useParams();

  const { onlineOrderID } = params;

  const { data, loading, error, refetch, previousData } = useOnlineOrderDetailPageQuery(onlineOrderID);
  const { data: updateStreamData } = useOnlineOrderUpdateSubscription(onlineOrderID);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);

  const lastUpdateTimestamp = useOnlineOrderDetailStore((state) => state.lastUpdateTimestamp);

  // Reload detection hook
  const { markAsUnsaved, markAsSaved, saveDataForReload } = useReloadDetection({
    storageKey: `online_order_${onlineOrderID || 'unknown'}`,
    warningMessage: 'Bạn có chắc chắn muốn rời khỏi trang? Thông tin đơn hàng chưa được lưu sẽ bị mất.',
    onReloadDetected: (data) => {
      console.log('Online order reload detected:', data);
      // Có thể gửi analytics hoặc log
    },
  });

  const onlineOrder = getFragmentData(OnlineOrderFragment, data?.storefront_onlineOrder);
  const prevOnlineOrder = getFragmentData(OnlineOrderFragment, previousData?.storefront_onlineOrder);
  const branch = getFragmentData(BranchFragment, onlineOrder?.branch);
  const configuration = getFragmentData(
    OnlineOrderBranchConfigurationFragment,
    data?.storefront_onlineOrderBranchConfigurationByOrderID
  );
  const availabilityData = getFragmentData(
    OnlineOrderAvailabilityFragment,
    data?.storefront_getOnlineOrderAvailability
  );

  // Lưu dữ liệu order để khôi phục sau reload
  useEffect(() => {
    if (onlineOrder && branch && configuration) {
      const orderData = {
        orderId: onlineOrder.id,
        userState: onlineOrder.userState,
        branchId: branch.id,
        companyId: branch.company.id,
        timestamp: Date.now(),
      };
      saveDataForReload(orderData);
      markAsUnsaved();
    }
  }, [onlineOrder, branch, configuration, saveDataForReload, markAsUnsaved]);

  // Đánh dấu đã lưu khi order hoàn thành
  useEffect(() => {
    if (onlineOrder?.userState === OnlineOrderUserState.Done) {
      markAsSaved();
    }
  }, [onlineOrder?.userState, markAsSaved]);

  useEffect(() => {
    if (branch) {
      if (branch.company.id) {
        setUserProperties(analytics, { companyId: branch.company.id, branchId: branch.id });
      }
    }
  }, [branch]);

  useEffect(() => {
    refetch().catch(captureException);
  }, [refetch, updateStreamData, lastUpdateTimestamp]);

  useEffect(() => {
    if (prevOnlineOrder?.userState !== onlineOrder?.userState) {
      window.scrollTo(0, 0);
    }
  }, [onlineOrder?.userState, prevOnlineOrder?.userState]);

  useEffect(() => {
    if (configuration?.template) {
      setIsLoadingTemplate(true);
      const templateId = normalizeTemplateEnum(configuration.template);
      loadTemplate(templateId)
        .then(() => {
          setIsLoadingTemplate(false);
        })
        .catch((error) => {
          console.error('Failed to load template from backend configuration:', error);
          setIsLoadingTemplate(false);
        });
    }
  }, [configuration?.template]);

  if (loading || isLoadingTemplate) {
    return <Loading />;
  }

  if (error) {
    return <ApplicationErrorView error={error} />;
  }

  if (!onlineOrder || !branch || !configuration || !availabilityData) {
    return <PageNotFound />;
  }

  if (!configuration.deliveryEnable && !configuration.pickupEnable) {
    return <UnavailableOnlineOrder website={branch.website ?? ''} />;
  }

  let page: ReactNode;

  switch (onlineOrder.userState) {
    case OnlineOrderUserState.PickingProduct:
      page = <ProductRepickerPage order={onlineOrder} />;
      break;

    case OnlineOrderUserState.FillContactInfo:
      page = (
        <FillContactInfoPage
          availabilityData={availabilityData}
          branch={branch}
          order={onlineOrder}
          onlineOrderBranchConfiguration={configuration}
        />
      );
      break;

    case OnlineOrderUserState.Review:
      page = <OnlineOrderReviewPage onlineOrder={onlineOrder} branch={branch} configuration={configuration} />;
      break;

    case OnlineOrderUserState.WaitingForPayment:
      page = <WaitingForPaymentPage onlineOrder={onlineOrder} branch={branch} configuration={configuration} />;
      break;

    case OnlineOrderUserState.WaitingForManualConfirm:
      // Once the customer has uploaded a proof, switch to the standard tracking page so they
      // see their full order + status. Until then, render the upload form.
      page = onlineOrder.manualTransferProofSubmittedAt ? (
        <OrderDetailPage onlineOrder={onlineOrder} branch={branch} configuration={configuration} />
      ) : (
        <ManualBankTransferPage onlineOrder={onlineOrder} branch={branch} configuration={configuration} />
      );
      break;

    case OnlineOrderUserState.Done:
      page = <OrderDetailPage onlineOrder={onlineOrder} branch={branch} configuration={configuration} />;
      break;

    default:
      page = <PageNotFound />;
      break;
  }

  return (
    <div className="order-detail-page">
      <CurrencyContext.Provider value={branch.company.settings.currency}>
        <div className="order-detail-page__container">{page}</div>
      </CurrencyContext.Provider>
    </div>
  );
};

export default OnlineOrderDetailPage;
